import { estimatePlan } from "./estimate.js";
import { ATHLETES_PER_FORMAT, PLAN_SCHEMA_VERSION } from "./schema.js";
import type { Category, Division, Format, Plan } from "./schema.js";
import { RACE_LEGS, RELAY_LEGS_PER_ATHLETE, STATIONS } from "./stations.js";
import type { StationId } from "./stations.js";

/** Starting targets for a new plan: roughly a 1:30 (90) finish. */
export const DEFAULT_RUN_PACE_SEC_PER_KM = 330;
/**
 * Roxzone before and after each station: 57 s per leg in all, except after the
 * last station, which goes straight to the finish.
 */
export const DEFAULT_ROXZONE_IN_SEC = 29;
export const DEFAULT_ROXZONE_OUT_SEC = 28;
export const DEFAULT_RELAY_HANDOVER_SEC = 10;
export const DEFAULT_STATION_SEC: Record<StationId, number> = {
  skierg: 310,
  "sled-push": 160,
  "sled-pull": 330,
  "burpee-broad-jumps": 390,
  row: 325,
  "farmers-carry": 135,
  "sandbag-lunges": 300,
  "wall-balls": 375,
};

export interface CreatePlanOptions {
  format: Format;
  category?: Category;
  division?: Division;
  name?: string;
  id?: string;
  now?: Date;
}

export function createPlan(options: CreatePlanOptions): Plan {
  const { format } = options;
  const now = (options.now ?? new Date()).toISOString();
  const athleteCount = ATHLETES_PER_FORMAT[format];
  const athletes = Array.from({ length: athleteCount }, (_, i) => ({
    id: `athlete-${i + 1}`,
    name: athleteCount === 1 ? "Me" : `Athlete ${i + 1}`,
  }));
  const evenSplit =
    format === "doubles"
      ? athletes.map((a) => ({ athleteId: a.id, pct: 100 / athleteCount }))
      : undefined;

  return {
    schemaVersion: PLAN_SCHEMA_VERSION,
    id: options.id ?? globalThis.crypto.randomUUID(),
    name: options.name ?? "My race plan",
    format,
    category: options.category ?? "open",
    division: options.division ?? "men",
    athletes,
    runs: Array.from({ length: RACE_LEGS }, () => ({
      paceSecPerKm: DEFAULT_RUN_PACE_SEC_PER_KM,
    })),
    stations: STATIONS.map((station, i) => ({
      stationId: station.id,
      targetSec: DEFAULT_STATION_SEC[station.id],
      roxzoneInSec: DEFAULT_ROXZONE_IN_SEC,
      roxzoneOutSec: i === RACE_LEGS - 1 ? 0 : DEFAULT_ROXZONE_OUT_SEC,
      ...(evenSplit && { split: evenSplit.map((s) => ({ ...s })) }),
      ...(format === "relay" && {
        athleteId: athletes[Math.floor(i / RELAY_LEGS_PER_ATHLETE)]!.id,
      }),
    })),
    ...(format === "relay" && { relayHandoverSec: DEFAULT_RELAY_HANDOVER_SEC }),
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Run paces that slow down linearly through the race: run n is
 * `basePace * (1 + fadePctPerRun / 100 * (n - 1))`.
 */
export function fadedRunPaces(basePaceSecPerKm: number, fadePctPerRun: number): number[] {
  return Array.from(
    { length: RACE_LEGS },
    (_, i) => basePaceSecPerKm * (1 + (fadePctPerRun / 100) * i),
  );
}

/**
 * Scales run, station and Roxzone targets proportionally so the plan finishes in
 * `goalSec`, keeping the shape of the existing plan. Relay handovers stay fixed.
 * Returns null if the goal cannot be reached (not more than the handover time).
 */
export function scalePlanToGoal(plan: Plan, goalSec: number): Plan | null {
  const estimate = estimatePlan(plan);
  const scalable = estimate.totalSec - estimate.handoverSec;
  const available = goalSec - estimate.handoverSec;
  if (scalable <= 0 || available <= 0) return null;
  const factor = available / scalable;

  return {
    ...plan,
    runs: plan.runs.map((run) => ({ ...run, paceSecPerKm: run.paceSecPerKm * factor })),
    stations: plan.stations.map((station) => ({
      ...station,
      targetSec: station.targetSec * factor,
      roxzoneInSec: station.roxzoneInSec * factor,
      roxzoneOutSec: station.roxzoneOutSec * factor,
    })),
  };
}

/**
 * Converts a plan to another format, keeping its run, station and Roxzone targets
 * and the names of athletes that remain. Doubles splits reset to 50/50 and relay
 * legs to athletes in pairs.
 */
export function changePlanFormat(plan: Plan, format: Format): Plan {
  if (plan.format === format) return plan;
  const fresh = createPlan({ format, id: plan.id, now: new Date(plan.createdAt) });
  const athletes = fresh.athletes.map((athlete, i) => ({
    ...athlete,
    name: plan.athletes[i]?.name ?? athlete.name,
  }));

  return {
    ...fresh,
    name: plan.name,
    category: plan.category,
    division: format === "singles" && plan.division === "mixed" ? "men" : plan.division,
    athletes,
    runs: plan.runs.map((run) => ({ ...run })),
    stations: plan.stations.map(({ split: _split, athleteId: _athleteId, ...station }, i) => ({
      ...station,
      ...(fresh.stations[i]?.split && { split: fresh.stations[i].split }),
      ...(fresh.stations[i]?.athleteId && { athleteId: fresh.stations[i].athleteId }),
    })),
    updatedAt: plan.updatedAt,
  };
}
